/**
 * 所蔵館 1 件を表すチップ。
 *
 * OPAC 詳細から取れた予約数・貸出可否を色とバッジで表現する。
 * 配色・ラベルの組み立ては `lib/holding-chip.ts` に置き、
 * BookCard (展開時の所蔵館一覧) と ReserveLibrarySheet (予約先の選択) で共有する。
 */

import { Loader2 } from 'lucide-react'

import { cn } from '@/lib/utils'

import {
  BADGE_TONE_CLASS,
  buildBadgeLabel,
  buildChipTitle,
  CHIP_TONE_CLASS,
  type ChipTone,
  isSafeHttpUrl,
  pickChipTone,
  type RankedEntry,
} from '../lib/holding-chip'
import { type Availability, deriveAvailability, type DerivedStats } from '../lib/library-stats'

/**
 * 予約数 / 所蔵冊数 / 貸出可否を表示するバッジ。
 *
 * - 統計未取得中は spinner、未対応 / エラー時は非表示
 */
export const StatsBadge = ({
  derived,
  isFetching,
  availability,
  tone,
}: {
  derived: DerivedStats | null
  isFetching: boolean
  availability: Availability
  tone: ChipTone
}) => {
  if (isFetching && !derived) {
    return <Loader2 className="size-3 animate-spin text-muted-foreground" />
  }
  if (!derived) return null

  const label = buildBadgeLabel(availability, derived)
  if (!label) return null

  return (
    <span className={cn('rounded px-1 text-[10px] font-semibold leading-none', BADGE_TONE_CLASS[tone])}>{label}</span>
  )
}

export const HoldingChip = ({ entry }: { entry: RankedEntry }) => {
  const { url, name, derived, isFetching } = entry
  const availability = deriveAvailability(derived)
  const tone = pickChipTone(availability, derived)

  const baseClass = cn(
    'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs text-white transition-colors',
    CHIP_TONE_CLASS[tone],
  )

  const label = (
    <span className="inline-flex items-center gap-1">
      {name}
      <StatsBadge derived={derived} isFetching={isFetching} availability={availability} tone={tone} />
    </span>
  )

  if (!url || !isSafeHttpUrl(url)) {
    return (
      <span className={cn(baseClass, 'opacity-60')} title={buildChipTitle('所蔵館', derived)}>
        {label}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()
        window.open(url, '_blank', 'noopener,noreferrer')
      }}
      className={cn(baseClass, 'cursor-pointer hover:brightness-125')}
      title={buildChipTitle('OPACを開く', derived)}
    >
      {label}
    </button>
  )
}
